import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { logger } from '@upadelmatch/core'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { matchesRepository } from './api/matches-repository'
import { seatIdentity } from '@/core/models/seat-identity'
import type { JoinerInfo } from '@/core/models/match-membership'
import { MATCH_CAPACITY, type MatchDoc } from '@/core/models/match'
import { resultWinnerNames, type RecordResultInput } from '@/core/models/match-result'
import { cityKeyForSpain, type PadelPlace } from '@/core/models/padel-place'
import type { LevelBand } from '@/shared/levels'
import type { MockMatch } from '@/shared/mock/data'

// `uid!` below is safe by the query's own gate: every one of these hooks sets
// `enabled: uid !== null` (or `!AUTH_BYPASS && uid !== null`), so TanStack
// Query never invokes the fn while uid is null. Asserting is preferable to a
// `?? ''` fallback, which would silently issue a query for the empty-string
// user and return an honest-looking empty result instead of not running.


// The view shape the certified cards/detail render — MockMatch with an
// honesty upgrade: distance is null until match-proximity ships (the cards
// omit the segment instead of fabricating a number).
export type MatchView = Omit<MockMatch, 'distanceKm'> & {
  distanceKm: number | null
  /** UID truth for membership decisions (names are display-only). */
  playerUids?: string[]
  organizerUid?: string
  padelPlaceId?: string | null
  manualAddress?: string | null
  cost?: number | null
  /** Organizer's group invite (spec/02 #11) — members-only surface. */
  whatsappGroupUrl?: string | null
  durationMinutes?: number
  /** Per-uid level movement stamped by the calibration CF (spec/05). */
  resultLevelDeltas?: Record<string, number> | null
  /** Display names of players flagged absent at recording (spec/06). */
  resultNoShowNames?: string[]
}

// `Mixto` was missing here until 2026-08-11, which is HALF of why `mixed`
// was unreachable: the enum had it, the rules checked it, and neither the
// form nor this map could produce it. Two mappings for one fact — the mock
// ternary in crear-partido-page and this one — is how a value goes missing
// from only the path that ships.
const GENDER_BY_LABEL = {
  Cualquiera: 'open',
  Mixto: 'mixed',
  Femenino: 'women',
  Masculino: 'men',
} as const

const POSITION_LABELS = { drive: 'Derecha', reverse: 'Revés' } as const
const POSITION_BY_LABEL: Record<string, 'drive' | 'reverse' | null> = {
  Derecha: 'drive',
  Revés: 'reverse',
  Indiferente: null,
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Normalize a WhatsApp group invite to `https://chat.whatsapp.com/<code>`
 * (spec/02, roadmap #11). Anything that isn't a group invite — profile
 * links, arbitrary URLs, junk — returns null and is stored as absent.
 */
export function sanitizeWhatsAppGroupUrl(input: string): string | null {
  const m = input.trim().match(/^(?:https?:\/\/)?(?:www\.)?chat\.whatsapp\.com\/([A-Za-z0-9]{10,})\/?$/)
  return m ? `https://chat.whatsapp.com/${m[1]}` : null
}

function dayLabelOf(d: Date): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(d)
  target.setHours(0, 0, 0, 0)
  const diff = Math.round((target.getTime() - today.getTime()) / 86400000)
  if (diff === 0) return 'Hoy'
  if (diff === 1) return 'Mañana'
  const label = target.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Pure doc → view mapping (exported for tests). */
export function matchDocToView(doc: MatchDoc): MatchView {
  const start = doc.startDateTime
  const playerEntries = doc.players.map((uid) => {
    const info = doc.playersInfo[uid]
    const identity = seatIdentity(info)
    return {
      uid,
      name: info?.name ?? 'Jugador',
      identity,
      // WHO vouched, resolved to a display name from the same roster — the seat
      // reads "Javi · invitado por Carlos" rather than an anonymous box.
      // Accountability nobody can see at the moment of deciding changes no
      // decision (spec/02 § What the player sees).
      createdByName:
        identity === 'placeholder' && info?.createdBy
          ? (doc.playersInfo[info.createdBy]?.name ?? null)
          : null,
      ...(info?.photoURL ? { src: info.photoURL } : {}),
      ...(identity === 'guest' ? { guest: true } : {}),
    }
  })
  return {
    id: doc.id,
    place: doc.location ?? doc.address ?? '—',
    city: doc.city ?? '',
    distanceKm: null,
    dayLabel: dayLabelOf(start),
    dateISO: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    timeLabel: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
    levelMin: doc.levelMin ?? null,
    levelMax: doc.levelMax ?? null,
    position: doc.positionSought ? POSITION_LABELS[doc.positionSought] : 'Indiferente',
    privacy: doc.privacy === 'private' ? 'Privado' : 'Abierto',
    matchType: doc.type === 'competitive' ? 'Competitivo' : 'Casual',
    players: playerEntries,
    // freeSlots() = capacity − players − companions must equal availableSlots.
    offAppCompanions: Math.max(MATCH_CAPACITY - doc.players.length - doc.availableSlots, 0),
    capacity: MATCH_CAPACITY,
    organizer: doc.playersInfo[doc.organizer]?.name ?? 'Organizador',
    // Reliability counters arrive with result recording — 0 is honest today.
    organizerMatches: 0,
    organizerNoShows: 0,
    courtReserved: doc.courtReserved,
    genderCategory: doc.genderCategory,
    playerUids: doc.players,
    organizerUid: doc.organizer,
    cost: doc.cost ?? null,
    whatsappGroupUrl: doc.whatsappGroupUrl ?? null,
    durationMinutes: doc.durationInMinutes,
    padelPlaceId: doc.padelPlaceId ?? null,
    manualAddress: doc.location ? null : (doc.address ?? null),
    status:
      doc.status === 'cancelled'
        ? 'cancelled'
        : doc.startDateTime.getTime() < Date.now()
          ? 'past'
          : doc.status === 'full'
            ? 'full'
            : 'active',
    ...(doc.description ? { note: doc.description } : {}),
    ...(doc.result
      ? {
          result: { winners: resultWinnerNames(doc), pairing: doc.result.pairingIndex },
          resultLevelDeltas: doc.result.levelDeltas ?? null,
          resultNoShowNames: (doc.result.noShows ?? []).map(
            (uid) => doc.playersInfo[uid]?.name ?? 'Jugador',
          ),
        }
      : {}),
  }
}

export interface CreateMatchFormInput {
  organizerUid: string
  organizerName: string
  organizerPhotoURL: string | null
  /** Resolved club doc when the picker chose a club; null for manual address. */
  place: PadelPlace | null
  manualAddress: string | null
  /** Local date + time strings from the form (yyyy-mm-dd, HH:mm). */
  dateISO: string
  time: string
  seatsSought: number
  /** 60 | 90 | 120 — padel's real slot lengths. */
  durationMinutes: number
  band: LevelBand | null
  positionLabel: string
  matchType: 'Casual' | 'Competitivo'
  privacy: 'Abierto' | 'Privado'
  genderLabel: 'Cualquiera' | 'Mixto' | 'Femenino' | 'Masculino'
  /** €/jugador, null = not stated (matches the WhatsApp 💰 slot). */
  costPerPlayer: number | null
  courtReserved: boolean
  note: string
  /** Raw group-invite input (spec/02 #11) — sanitized at build time. */
  whatsappGroupUrl?: string
}

/** Pure form → doc payload mapping (exported for tests). */
export function buildCreateMatchDoc(input: CreateMatchFormInput): Record<string, unknown> {
  const start = new Date(`${input.dateISO}T${input.time}:00`)
  const loc = input.place?.location ?? null
  return {
    location: input.place?.name ?? null,
    address: input.manualAddress,
    city: input.place?.city ?? null,
    country: input.place?.country ?? null,
    padelPlaceId: input.place?.id ?? null,
    countryCode: input.place?.countryCode ?? null,
    // Manual-address matches still need a partition key for the city feed —
    // fall back to the organizer's picked city via the place, else null
    // (they surface once proximity search lands).
    cityKey: input.place?.cityKey ?? null,
    latitude: loc?.latitude ?? null,
    longitude: loc?.longitude ?? null,
    geohash: loc?.geohash ?? null,
    startDateTime: start,
    durationInMinutes: input.durationMinutes,
    type: input.matchType === 'Competitivo' ? 'competitive' : 'casual',
    levelMin: input.band ? input.band.min : null,
    levelMax: input.band ? input.band.max : null,
    positionSought: POSITION_BY_LABEL[input.positionLabel] ?? null,
    genderCategory: GENDER_BY_LABEL[input.genderLabel] ?? 'open',
    players: [input.organizerUid],
    playersInfo: {
      [input.organizerUid]: {
        name: input.organizerName,
        photoURL: input.organizerPhotoURL,
      },
    },
    availableSlots: input.seatsSought,
    organizer: input.organizerUid,
    cost: input.costPerPlayer,
    whatsappGroupUrl: sanitizeWhatsAppGroupUrl(input.whatsappGroupUrl ?? ''),
    privacy: input.privacy === 'Privado' ? 'private' : 'public',
    status: 'active',
    description: input.note.trim() || null,
    courtReserved: input.courtReserved,
  }
}

/** Upcoming public matches for a city — the real discovery feed base set. */
export function useUpcomingMatches(city: string) {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['matches', cityKeyForSpain(city)],
    queryFn: async () => {
      const docs = await matchesRepository.listUpcomingByCity(cityKeyForSpain(city), new Date())
      return docs
        .filter((d) => d.privacy === 'public' && d.status === 'active' && d.availableSlots > 0)
        .map(matchDocToView)
    },
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 60_000,
  })
}


/**
 * Optimistic seat maths for join/leave.
 *
 * A padel court has spotty WiFi, and until now tapping "Unirme" showed nothing
 * at all until the Firestore round-trip landed — on a bad connection that reads
 * as a dead button, and the player taps again. These apply the change to the
 * cached match immediately and hand back a rollback.
 *
 * Every mutation here already reports failures (`reportMutationError`), so a
 * rejected write both rolls the UI back AND reaches Sentry.
 */
async function optimisticMembership(
  qc: ReturnType<typeof useQueryClient>,
  matchId: string,
  apply: (m: MatchView) => MatchView,
): Promise<() => void> {
  // ⚠️ SNAPSHOT AND WRITE MUST USE THE SAME KEY SEMANTICS.
  //
  // The first version of this snapshotted with `getQueryData(['matches'])` —
  // an EXACT key lookup — while writing with `setQueriesData({queryKey:
  // ['matches']})`, which is a PREFIX match. The real keys carry a suffix
  // (`['matches', cityKey]`, `['my-matches', uid]`), so the snapshots came back
  // undefined while the writes landed: a failed join rolled back the detail
  // screen and left a phantom player sitting in the feed and Mis partidos with
  // nothing to correct it. Shipped because nothing tested it, on a branch whose
  // own docs say a fix without a failing test is a belief. `getQueriesData`
  // pairs correctly with `setQueriesData`.
  //
  // `cancelQueries` first: the feed has a 60s staleTime, so an in-flight
  // refetch could otherwise land after the optimistic write and clobber it.
  await Promise.all([
    qc.cancelQueries({ queryKey: ['match', matchId] }),
    qc.cancelQueries({ queryKey: ['matches'] }),
    qc.cancelQueries({ queryKey: ['my-matches'] }),
  ])

  const previous: [readonly unknown[], unknown][] = [
    ...qc.getQueriesData({ queryKey: ['match', matchId] }),
    ...qc.getQueriesData({ queryKey: ['matches'] }),
    ...qc.getQueriesData({ queryKey: ['my-matches'] }),
  ]

  qc.setQueryData(['match', matchId], (old: MatchView | null | undefined) =>
    old ? apply(old) : old,
  )
  const patchList = (list: MatchView[] | undefined) =>
    list?.map((m) => (m.id === matchId ? apply(m) : m))
  qc.setQueriesData({ queryKey: ['matches'] }, patchList)
  qc.setQueriesData({ queryKey: ['my-matches'] }, patchList)

  return () => {
    for (const [key, data] of previous) qc.setQueryData(key, data)
  }
}

export function useCreateMatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const id = matchesRepository.newId()
      await matchesRepository.create(id, payload)
      return id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['matches'] })
    },
    onError: reportMutationError('create'),
  })
}


/** One match by id — detail page. Mock under the bypass (see usePlace). */
export function useMatch(id: string | undefined) {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['match', id],
    queryFn: async () => {
      // `id!` — the query is `enabled` only when `!!id`; the file header covers
      // `uid!`, this one is its own gate.
      const doc = await matchesRepository.getById(id!)
      return doc ? matchDocToView(doc) : null
    },
    enabled: !AUTH_BYPASS && uid !== null && !!id,
    staleTime: 15_000,
  })
}

/** My matches (organized or joined) — Mis partidos' base set. */
export function useMyMatches() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['my-matches', uid],
    queryFn: async () => (await matchesRepository.listMine(uid!)).map(matchDocToView),
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 30_000,
  })
}

/** The three caches every membership/result write can move. */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['matches'] })
  void qc.invalidateQueries({ queryKey: ['my-matches'] })
  void qc.invalidateQueries({ queryKey: ['match'] })
}

function useInvalidateMatches() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['matches'] })
    void qc.invalidateQueries({ queryKey: ['my-matches'] })
    void qc.invalidateQueries({ queryKey: ['match'] })
  }
}

/**
 * Every match mutation is a money/trust-shaped write (a seat taken, a match
 * cancelled on three other people, a result that moves everyone's level).
 * React Query parks a rejection in `mutation.error` — which nothing rendered,
 * so before this a failed join was invisible to the player AND absent from
 * Sentry. `logger.e` scrubs PII before it leaves the device.
 *
 * Callers still own the USER-facing message; this only guarantees the failure
 * is never silent to us. Pass extra context as `meta`.
 */
function reportMutationError(op: string, meta?: Record<string, unknown>) {
  return (error: unknown) => {
    // Pass the Error THROUGH, don't stringify it: the shared logger routes an
    // Error arg to captureException (stack + grouping) and everything else to
    // captureMessage (no stack, and the context gets flattened into the title).
    logger.e(`[matches] ${op} failed`, error instanceof Error ? error : new Error(String(error)), {
      ...meta,
    })
  }
}

export function useJoinMatch() {
  const invalidate = useInvalidateMatches()
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { matchId: string; joiner: import('@/core/models/match-membership').JoinerInfo }) =>
      matchesRepository.joinMatch(args.matchId, args.joiner),
    onMutate: (args) =>
      optimisticMembership(qc, args.matchId, (m) => ({
        ...m,
        players: [...m.players, { name: args.joiner.name }],
        playerUids: [...(m.playerUids ?? []), args.joiner.uid],
      })),
    // Invalidate on SETTLED, not just success: a rolled-back failure still
    // needs the server's truth to replace the optimistic guess.
    onSettled: () => invalidateAll(qc),
    onSuccess: (_data, args) => {
      invalidate()
      // Joining a match I was invited to closes the invite (best-effort —
      // the membership transaction above is the truth).
      void import('./use-match-invites')
        .then(({ markInviteAccepted }) => markInviteAccepted(args.matchId, uid))
        .catch(reportMutationError('join:markInviteAccepted'))
    },
    onError: (error, args, rollback) => {
      rollback?.()
      reportMutationError('join', { matchId: args.matchId })(error)
    },
  })
}

/** Organizer ±plaza for off-app companions (spec/02, 2026-07-05). */
export function useAdjustSeats() {
  const invalidate = useInvalidateMatches()
  return useMutation({
    mutationFn: (args: { matchId: string; delta: 1 | -1 }) =>
      matchesRepository.adjustSeats(args.matchId, args.delta),
    onSuccess: invalidate,
    onError: (error, args) =>
      reportMutationError('adjustSeats', { matchId: args.matchId, delta: args.delta })(error),
  })
}

export function useLeaveMatch() {
  const invalidate = useInvalidateMatches()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { matchId: string; uid: string }) =>
      matchesRepository.leaveMatch(args.matchId, args.uid),
    onMutate: (args) =>
      optimisticMembership(qc, args.matchId, (m) => {
        // Drop the LEAVING player, not simply the last one. Slicing the tail
        // kept the seat count right (freeSlots derives from players.length) but
        // made the wrong person's avatar disappear. playerUids and players are
        // index-aligned, so find the index once and drop it from both.
        const idx = (m.playerUids ?? []).indexOf(args.uid)
        return {
          ...m,
          playerUids: (m.playerUids ?? []).filter((u) => u !== args.uid),
          players: idx >= 0 ? m.players.filter((_, i) => i !== idx) : m.players.slice(0, -1),
        }
      }),
    onSettled: () => invalidateAll(qc),
    onSuccess: invalidate,
    onError: (error, args, rollback) => {
      rollback?.()
      reportMutationError('leave', { matchId: args.matchId })(error)
    },
  })
}

export function useCancelMatch() {
  const invalidate = useInvalidateMatches()
  return useMutation({
    mutationFn: (matchId: string) => matchesRepository.cancel(matchId),
    onSuccess: invalidate,
    onError: (error, matchId) => reportMutationError('cancel', { matchId })(error),
  })
}

export function useRecordResult() {
  const invalidate = useInvalidateMatches()
  return useMutation({
    mutationFn: (args: { matchId: string; input: RecordResultInput }) =>
      matchesRepository.recordResult(args.matchId, args.input),
    onSuccess: invalidate,
    onError: (error, args) => reportMutationError('recordResult', { matchId: args.matchId })(error),
  })
}


/** "Mañana · dom, 6 jul" — the exact date beside the RELATIVE label. Labels
 *  that already carry a weekday ("Viernes 4") render the absolute form alone:
 *  "Viernes 4 · vie, 4 jul" said the weekday twice (styleguide §0 redundancy,
 *  sweep-2 2026-07-05). */
export function dayLabelWithDate(dayLabel: string, dateISO: string): string {
  const d = new Date(dateISO + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return dayLabel
  if (/^(Hoy|Mañana)/.test(dayLabel)) {
    const abs = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })
    return `${dayLabel} · ${abs}`
  }
  const full = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })
  return full.charAt(0).toUpperCase() + full.slice(1)
}


/**
 * Seat somebody who is not on the app, and CLAIM such a seat.
 *
 * Adding needs no confirmation from the person named — the job is closing the
 * seat and it is urgent: leave it open until they confirm and a stranger takes
 * it, leaving five people for four places (spec/02). The false assertion is
 * priced instead: name someone who does not turn up and the no-show is yours.
 */
export function useAddPlaceholder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      matchId: string
      name: string
      createdBy: string
      /** Mixto only — a seat with no gender cannot be counted into 2+2. */
      gender?: 'male' | 'female'
    }) =>
      matchesRepository.addPlaceholder(args.matchId, {
        name: args.name,
        createdBy: args.createdBy,
        ...(args.gender ? { gender: args.gender } : {}),
      }),
    onSuccess: (_uid, args) => {
      void qc.invalidateQueries({ queryKey: ['match', args.matchId] })
      void qc.invalidateQueries({ queryKey: ['matches'] })
    },
    onError: (error) => reportMutationError('addPlaceholder', {})(error),
  })
}

export function useRemovePlaceholder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { matchId: string; placeholderUid: string }) =>
      matchesRepository.removePlaceholder(args.matchId, args.placeholderUid),
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({ queryKey: ['match', args.matchId] })
      void qc.invalidateQueries({ queryKey: ['matches'] })
    },
    onError: (error) => reportMutationError('removePlaceholder', {})(error),
  })
}

export function useClaimPlaceholder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { matchId: string; placeholderUid: string; claimer: JoinerInfo }) =>
      matchesRepository.claimPlaceholder(args.matchId, {
        placeholderUid: args.placeholderUid,
        claimer: args.claimer,
      }),
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({ queryKey: ['match', args.matchId] })
      void qc.invalidateQueries({ queryKey: ['match-preview'] })
      void qc.invalidateQueries({ queryKey: ['matches'] })
    },
    onError: (error) => reportMutationError('claimPlaceholder', {})(error),
  })
}
